import {
  Button,
  Dialog,
  DialogButtons,
  PlusIcon,
  Select,
  Spinner,
  TextInput,
  TrashIcon
} from '@st/theme'
import { Folder } from '@st/sdk'
import { inflect } from '@st/util/inflect'
import { useState } from 'react'
import { useProcess, useSelect } from '~/store'
import {
  bulkAddChecklistItemsDocTypesKey,
  selIsBulkAddingChecklistItems,
  stFolderListModule
} from './st-folder-list-module'

type DraftItem = { documentTypeId: string | undefined; note: string }

export function STBulkAddChecklistItemsDialog({
  folders,
  onClose
}: {
  folders: Array<Pick<Folder, 'id' | 'schemaId'>>
  onClose: () => void
}) {
  const folderList = useProcess('stFolderList')

  const folderIds = folders.map((f) => f.id)
  const schemaIds = new Set(folders.map((f) => f.schemaId))
  const schemaId = schemaIds.size == 1 ? Array.from(schemaIds)[0] : undefined

  const docTypes = useSelect('stFolderList', (s) => s.bulkAddChecklistItemsDocTypes)
  const docTypesKey = schemaId
    ? bulkAddChecklistItemsDocTypesKey(schemaId, folderIds)
    : 'invalid-schema'

  const [items, setItems] = useState<DraftItem[]>([{ documentTypeId: undefined, note: '' }])
  const submitting = useSelect('stFolderList', (s) => selIsBulkAddingChecklistItems(s, folderIds))

  if (!schemaId) {
    return (
      <Dialog
        title="Add checklist items"
        buttons={
          <DialogButtons>
            <Button onClick={onClose}>Close</Button>
          </DialogButtons>
        }
      >
        <div className="text-red-600">
          Selected binders have a mix of return types and tax years. Please select only one return
          type and tax year.
        </div>
      </Dialog>
    )
  }

  if (docTypes.status == 'failed' && docTypes.key == docTypesKey) {
    return (
      <Dialog
        title="Add checklist items"
        buttons={
          <DialogButtons>
            <Button onClick={onClose}>Close</Button>
          </DialogButtons>
        }
      >
        <div className="text-red-600">Unable to load document types.</div>
      </Dialog>
    )
  }

  if (docTypes.status != 'loaded' || docTypes.key != docTypesKey) {
    return <Dialog title="Add checklist items" loading />
  }

  const documentTypes = docTypes.documentTypes
    .filter((dt) => dt.id != 'stanfordtax')
    .sort((a, b) => a.name.localeCompare(b.name))

  const folderCount = `${folderIds.length} ${inflect(folderIds.length, 'binder')}`

  function onSubmit() {
    const payloadItems = items.map((item) => {
      if (!item.documentTypeId) {
        throw new Error('Expected documentTypeId to be set before submitting checklist items')
      }

      const documentType = documentTypes.find((dt) => dt.id == item.documentTypeId)
      if (!documentType) {
        throw new Error(`Unknown document type: ${item.documentTypeId}`)
      }

      return {
        name: documentType.name,
        note: item.note,
        context: { type: 'document_request' as const, documentTypeId: item.documentTypeId, match: {} },
        phase: 'intake' as const,
        visibility: 'all' as const,
        attachments: []
      }
    })

    folderList.send({
      type: 'bulkCreateChecklistItems',
      folderIds,
      items: payloadItems
    })
  }

  return (
    <Dialog
      title="Add checklist items"
      subtitle={`These will be added to ${folderCount}.`}
      buttons={
        <DialogButtons>
          <Button variant="subtle" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            leadingIcon={submitting ? <Spinner className="size-4" /> : undefined}
            disabled={submitting || !items.every((i) => i.documentTypeId)}
            onClick={onSubmit}
          >
            {submitting ? 'Adding...' : `Add to ${folderCount}`}
          </Button>
        </DialogButtons>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-[13rem_1fr_2.5rem] items-center gap-2 text-xs text-gray-600">
          <div>Name</div>
          <div>Note</div>
          <div />
        </div>
        <div className="flex flex-col gap-2">
          {items.map((item, idx) => (
            <div key={idx} className="grid grid-cols-[13rem_1fr_2.5rem] items-center gap-2">
              <Select
                className="w-full"
                placeholder="Document type"
                options={documentTypes}
                buildValue={(dt) => dt.id}
                buildLabel={(dt) => dt.name}
                value={item.documentTypeId}
                onChange={(value) =>
                  setItems((prev) =>
                    prev.map((it, i) => (i == idx ? { ...it, documentTypeId: value } : it))
                  )
                }
              />
              <TextInput
                placeholder="Note"
                className="w-full"
                value={item.note}
                onChange={(value) =>
                  setItems((prev) => prev.map((it, i) => (i == idx ? { ...it, note: value } : it)))
                }
              />
              <Button
                aria-label="Remove checklist item"
                variant="subtle"
                disabled={submitting || items.length == 1}
                onClick={() => setItems((prev) => prev.filter((_, i) => i != idx))}
              >
                <TrashIcon className="size-4" />
              </Button>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-[13rem_1fr_2.5rem] items-center gap-2">
          <Button
            className="w-full"
            variant="default"
            leadingIcon={<PlusIcon className="size-4" />}
            disabled={submitting}
            onClick={() => setItems((prev) => [...prev, { documentTypeId: undefined, note: '' }])}
          >
            Add item
          </Button>
          <div />
          <div />
        </div>
      </div>
    </Dialog>
  )
}
